import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useOutletContext, useParams, useSearchParams } from 'react-router';
import ROUTES from '../constants/routes';
import { ConditionalRequireAuth, RequireAuth, RequireAuthAdmin, RequireAuthAllStaff, RequireAuthHelpStaff, RequireAuthIntegrationTools, RequireAuthMarketingManager, RequireAuthRep, RequireAuthRepPerformance, RequireAuthStaffManager, RequireAuthSuperAdmin, RequireAuthWithAffiliateRoles, RequireAuthWithCoveredEntityRoles } from '../routes/RouteSecurity';
import LandingLayout from './marketing/LandingLayout';
import HomeLayout from './marketing/HomeLayout';
import BlankLayout from './marketing/BlankLayout';
import ProductLandingLayout from './marketing/ProductLandingLayout';
import FunnelLayout from './marketing/FunnelLayout';
import AuthLayout from './dashboard/AuthLayout';
import AuthSplitLayout from './auth/AuthSplitLayout';
import AuthCenteredLayout from './auth/AuthCenteredLayout';
import AccountLayout from './account/AccountLayout';
import AdminLayout from './dashboard/AdminLayout';
import EncounterLayout from './marketing/EncounterLayout';
import WrapperLayout from './dashboard/WrapperLayout';
import { useAuth } from '../redux/ducks/auth';
import { useOrchestrator } from '../redux/ducks/orchestrator';
import { isListEmpty, isBooleanFalse, isStringNotEmpty } from '../utils/objectUtils';
import { hasHelpStaffPermissions } from '../utils/permissions';
import { getSupportGroupsAccessDecision, SUPPORT_GROUP_SUBSCRIPTION_STATUSES } from '../utils/supportGroupsAccessUtils';
import { getProgressLogAccessDecision, PROGRESS_LOG_SUBSCRIPTION_STATUSES } from '../utils/progressLogAccessUtils';
import { BrandLoader } from '../components/BrandLoader/BrandLoader';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import { EncounterPage } from '../utils/routeChunkUtils';

// import ProgramFunnelPage from '../pages/Products/Funnels';

const SignInPage = lazyWithRetry(() => import('../pages/SignIn'));
const ForgotPasswordPage = lazyWithRetry(() => import('../pages/ForgotPassword'));
const SetNewPasswordPage = lazyWithRetry(() => import('../pages/SetNewPassword'));
const AccountHomePage = lazyWithRetry(() => import('../pages/AccountHome'));
const MyCheckInsPage = lazyWithRetry(() => import('../pages/Account/Settings/CheckIns'));
const SecurityPage = lazyWithRetry(() => import('../pages/Account/Security'));
const ChangePasswordPage = lazyWithRetry(() => import('../pages/Account/ChangePassword'));
const ChangePhoneNumberPage = lazyWithRetry(() => import('../pages/Account/ChangePhoneNumber'));

const ContactPage = lazyWithRetry(() => import('../pages/Contact'));
const AgreementPage = lazyWithRetry(() => import('../pages/Agreements/Agreement'));
const MultipleAgreementsPage = lazyWithRetry(() => import('../pages/Agreements/MultipleAgreements'));
const LeadsPage = lazyWithRetry(() => import('../pages/Leads'));
const NotFoundPage = lazyWithRetry(() => import('../pages/NotFound'));
const NoAccessPage = lazyWithRetry(() => import('../pages/NoAccess'));
const SignUpPage = lazyWithRetry(() => import('../pages/SignUp'));
const UsersPage = lazyWithRetry(() => import('../pages/Users'));
const VerifyEmailPage = lazyWithRetry(() => import('../pages/VerifyEmail'));
const VerifyPhonePage = lazyWithRetry(() => import('../pages/VerifyPhone'));
const AccountSetupPage = lazyWithRetry(() => import('../pages/AccountSetup'));
const UserActionPage = lazyWithRetry(() => import('../pages/UserAction'));
const SubscriptionsPage = lazyWithRetry(() => import('../pages/Subscriptions'));
const CancelledSubscriptionsPage = lazyWithRetry(() => import('../pages/Subscriptions/Cancellations'));
const CancellationRequestsPage = lazyWithRetry(() => import('../pages/CancellationRequests'));
const OrdersPage = lazyWithRetry(() => import('../pages/Orders'));
const OrderGroupsPage = lazyWithRetry(() => import('../pages/OrderGroups'));
const ReportsPage = lazyWithRetry(() => import('../pages/Reports'));
const AuthUsersPage = lazyWithRetry(() => import('../pages/AuthUsers'));
const CoveredEntitiesPage = lazyWithRetry(() => import('../pages/CoveredEntities'));
const MessagesPage = lazyWithRetry(() => import('../pages/Messages'));
const CoveredEntityPage = lazyWithRetry(() => import('../pages/CoveredEntity'));
const CoveredEntityUsersPage = lazyWithRetry(() => import('../pages/CoveredEntityUsers'));
const SalesByStateDashboardPage = lazyWithRetry(() => import('../pages/Admin/SalesByStateDashboard'));
const GenerateAgreementPage = lazyWithRetry(() => import('../pages/Agreements/generate'));
const ProductDetailsPage = lazyWithRetry(() => import('../pages/Products/ProductPages'));
const ShopPage = lazyWithRetry(() => import('../pages/Products/ProductGrid'));

const ChatPage = lazyWithRetry(() => import('../pages/Chat'));
const MyReferralsPage = lazyWithRetry(() => import('../pages/MyReferrals'));
const TreatmentsPage = lazyWithRetry(() => import('../pages/Treatments'));
const MyEventsPage = lazyWithRetry(() => import('../pages/MyEvents'));
const StorePage = lazyWithRetry(() => import('../pages/Store'));
const MySupportPage = lazyWithRetry(() => import('../pages/MySupport'));
const SubscriptionReactivationPage = lazyWithRetry(() => import('../pages/SubscriptionReactivation'));
const SubscriptionReactivationDeepLinkPage = lazyWithRetry(() => import('../pages/Subscriptions/SubscriptionReactivationPage'));


const EncountersPage = lazyWithRetry(() => import('../pages/Encounters'));
const PaymentsPage = lazyWithRetry(() => import('../pages/Payments'));
const TransactionsPage = lazyWithRetry(() => import('../pages/Transactions'));
const VouchersPage = lazyWithRetry(() => import('../pages/Vouchers'));
const ProgramsPage = lazyWithRetry(() => import('../pages/Programs'));
const AffiliatesPage = lazyWithRetry(() => import('../pages/Affiliates'));
const CampaignsPage = lazyWithRetry(() => import('../pages/Campaigns'));
const SMSTemplatesPage = lazyWithRetry(() => import('../pages/SMSTemplates'));
const VendorsPage = lazyWithRetry(() => import('../pages/Vendors'));
const LeadAggregatesPage = lazyWithRetry(() => import('../pages/Leads/Aggregates'));
const LeadAffiliateAggregatesPage = lazyWithRetry(() => import('../pages/Leads/AffiliateAggregates'));
const LeadTablePage = lazyWithRetry(() => import('../pages/Leads/LeadTable'));
const SurveysPage = lazyWithRetry(() => import('../pages/Surveys'));
const ProductsPage = lazyWithRetry(() => import('../pages/Products'));
const EnrollmentGroupsPage = lazyWithRetry(() => import('../pages/EnrollmentGroups'));
const ProductFamiliesPage = lazyWithRetry(() => import('../pages/ProductFamilies'));
const OfferGroupsPage = lazyWithRetry(() => import('../pages/OfferGroups'));
const CommercialEquivalencyGroupsPage = lazyWithRetry(() => import('../pages/CommercialEquivalencyGroups'));
const FunnelChoiceSetsPage = lazyWithRetry(() => import('../pages/FunnelChoiceSets'));
const MedicationTransitionRulesPage = lazyWithRetry(() => import('../pages/MedicationTransitionRules'));
const ProductIdentityAuditPage = lazyWithRetry(() => import('../pages/ProductIdentityAudit'));
const ProductCategoriesPage = lazyWithRetry(() => import('../pages/ProductCategories'));
const ShippingClassesPage = lazyWithRetry(() => import('../pages/ShippingClasses'));
const ShippingMethodsPage = lazyWithRetry(() => import('../pages/ShippingMethods'));
const CouponsPage = lazyWithRetry(() => import('../pages/Coupons'));
const EmailTemplatesPage = lazyWithRetry(() => import('../pages/EmailTemplates'));
const AgreementsPage = lazyWithRetry(() => import('../pages/Agreements'));
const PrescriptionsPage = lazyWithRetry(() => import('../pages/Prescriptions'));
const PrescriptionGroupsPage = lazyWithRetry(() => import('../pages/PrescriptionGroups'));
const HelpPage = lazyWithRetry(() => import('../pages/Help'));
const CouponUsesPage = lazyWithRetry(() => import('../pages/CouponUses'));
const AnalyticsPage = lazyWithRetry(() => import('../pages/Analytics'));
const AdminsListPage = lazyWithRetry(() => import('../pages/Users/AdminsList'));
const RepsListPage = lazyWithRetry(() => import('../pages/Users/RepsList'));
const AffiliateConversionsPage = lazyWithRetry(() => import('../pages/AffiliateConversions'));
const RepConversionsPage = lazyWithRetry(() => import('../pages/RepConversions'));
const RepPayoutsPage = lazyWithRetry(() => import('../pages/RepPayouts'));

const ProgramFunnelPage = lazyWithRetry(() => import('../pages/Products/Funnels'));

const AffiliatePayoutsPage = lazyWithRetry(() => import('../pages/AffiliatePayouts'));
const AffiliateUsersPage = lazyWithRetry(() => import('../pages/AffiliateUsers'));
const AffiliatePage = lazyWithRetry(() => import('../pages/Affiliate'));

const AssetsPage = lazyWithRetry(() => import('../pages/Articles/Assets'));
const BlogCategoryPage = lazyWithRetry(() => import('../pages/Blog/Category'));
const BlogPage = lazyWithRetry(() => import('../pages/Blog'));
const BlogPostPage = lazyWithRetry(() => import('../pages/Blog/Article'));
const ArticleCategoriesPage = lazyWithRetry(() => import('../pages/ArticleCategories'));
const ArticlesPage = lazyWithRetry(() => import('../pages/Articles'));

const HomePage = lazyWithRetry(() => import('../pages/Home'));
const StaffDashboardPage = lazyWithRetry(() => import('../pages/Admin/StaffDashboard'));
const AdminStatisticsPage = lazyWithRetry(() => import('../pages/Admin/Statistics'));
const RetentionStatisticsPage = lazyWithRetry(() => import('../pages/Admin/RetentionStatistics'));
const RepStatisticsPage = lazyWithRetry(() => import('../pages/Admin/RepStatistics'));
const RepPerformancePage = lazyWithRetry(() => import('../pages/Admin/RepPerformance'));
const RepSlaDashboardPage = lazyWithRetry(() => import('../pages/Admin/RepSlaDashboard'));
const RepDashboardPreviewPage = lazyWithRetry(() => import('../pages/Admin/RepDashboardPreview'));
const FunnelStatisticsPage = lazyWithRetry(() => import('../pages/Analytics/FunnelStatistics'));
const FinancialsPage = lazyWithRetry(() => import('../pages/Financials'));
const AgentDashboardPage = lazyWithRetry(() => import('../pages/Admin/AgentDashboard'));
const AgentCheckoutPage = lazyWithRetry(() => import('../pages/AgentCheckout'));

const FunnelAnalyticsPage = lazyWithRetry(() => import('../pages/Analytics/FunnelAnalytics'));
const SiteVisitsPage = lazyWithRetry(() => import('../pages/Analytics/SiteVisits'));
const SiteEventsPage = lazyWithRetry(() => import('../pages/Analytics/SiteEvents'));
const SiteEventStepSummaryPage = lazyWithRetry(() => import('../pages/Analytics/SiteEventStepSummary'));

const CheckInsPage = lazyWithRetry(() => import('../pages/CheckIns'));
const WeightLossCheckInPage = lazyWithRetry(() => import('../pages/CheckIn/WeightLossCheckIn'));

const MealPlansPage = lazyWithRetry(() => import('../pages/MealPlans'));
const MealPlanSetupPage = lazyWithRetry(() => import('../pages/MealPlan/MealPlanSetup'));
const MyMealPlansPage = lazyWithRetry(() => import('../pages/MyMealPlans'));
const MealPlanRequestPage = lazyWithRetry(() => import('../pages/MyMealPlans/request'));
const MealsPage = lazyWithRetry(() => import('../pages/Meals'));


const AccountsPayableOrdersPage = lazyWithRetry(() => import('../pages/Orders/AccountsPayableOrderTable'));
const AccountsPayableEncountersPage = lazyWithRetry(() => import('../pages/Encounters/AccountsPayableEncounterTable'));


const WebhookLogsPage = lazyWithRetry(() => import('../pages/WebhookLogs'));
const ApiLogsPage = lazyWithRetry(() => import('../pages/ApiLogs'));
const ChangelogLogsPage = lazyWithRetry(() => import('../pages/ChangelogLogs'));

const ProvidersPage = lazyWithRetry(() => import('../pages/Providers'));
const BiomarkersPage = lazyWithRetry(() => import('../pages/Biomarkers'));
const LabPanelsPage = lazyWithRetry(() => import('../pages/LabPanels'));

const HabitTemplatePage = lazyWithRetry(() => import('../pages/HabitTracker/Admin/HabitTemplateTable'));
const UserHabitPage = lazyWithRetry(() => import('../pages/HabitTracker/Admin/UserHabitTable'));
const WalletPage = lazyWithRetry(() => import('../pages/HabitTracker/Admin/WalletTable'));

const GLP1Page = lazyWithRetry(() => import('../pages/Products/ProductPages/GLP1'));
const ReferralsPage = lazyWithRetry(() => import('../pages/Referrals'));
//const ManageReferralsPage = lazyWithRetry(() => import('../pages/Referrals/manage'));

const EventsPage = lazyWithRetry(() => import('../pages/Events'));
const EventCheckInPage = lazyWithRetry(() => import('../pages/EventCheckIn'));
const CheckInTable = lazyWithRetry(() => import('../pages/CheckIns/CheckInTable'));

const ReferralCodesPage = lazyWithRetry(() => import('../pages/Coupons/ReferralCodes'));

const CommunityPage = lazyWithRetry(() => import('../pages/Community'));
const CommunitiesPage = lazyWithRetry(() => import('../pages/Communities'));
const GroupsPage = lazyWithRetry(() => import('../pages/Groups'));
const GroupPage = lazyWithRetry(() => import('../pages/Group'));

const BrowseCommunitiesPage = lazyWithRetry(() => import('../pages/Community/Browse'));

const TasksPage = lazyWithRetry(() => import('../pages/Tasks'));
const SupportDashboardPage = lazyWithRetry(() => import('../pages/Support'));
const SupportInboxWorkspacePage = lazyWithRetry(() => import('../pages/Support/SupportInboxWorkspace'));
const SupportSplitWorkspacePage = lazyWithRetry(() => import('../pages/Support/SupportSplitWorkspace'));
const SupportInboxOversightPage = lazyWithRetry(() => import('../pages/Support/SupportInboxOversight'));
const SupportTicketDetailPage = lazyWithRetry(() => import('../pages/Support/SupportTicketDetail'));
const SupportReplyTemplatesPage = lazyWithRetry(() => import('../pages/Support/ReplyTemplates'));
const SupportSnippetsPage = lazyWithRetry(() => import('../pages/Support/Snippets'));
const LeadsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/LeadsWorkspace'));
const SubscriptionsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/SubscriptionsWorkspace'));
const CancelledSubscriptionsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/CancelledSubscriptionsWorkspace'));
const CancellationRequestsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/CancellationRequestsWorkspace'));
const OrdersWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/OrdersWorkspace'));
const OrderGroupsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/OrderGroupsWorkspace'));
const EncountersWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/EncountersWorkspace'));
const PrescriptionsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/PrescriptionsWorkspace'));
const PaymentsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/PaymentsWorkspace'));
const TransactionsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/TransactionsWorkspace'));
const VouchersWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/VouchersWorkspace'));
const CheckInsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/CheckInsWorkspace'));
const MealPlansWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/MealPlansWorkspace'));
const MealsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/MealsWorkspace'));
const EventsWorkspacePage = lazyWithRetry(() => import('../pages/OperationsWorkspaces/EventsWorkspace'));
const AutomationListPage = lazyWithRetry(() => import('../pages/Automations'));
const AutomationDetailPage = lazyWithRetry(() => import('../pages/Automations/AutomationDetail'));
const KnowledgeBasePage = lazyWithRetry(() => import('../pages/KnowledgeBase'));
const HelpCategoryPage = lazyWithRetry(() => import('../pages/Help/HelpCategory'));
const HelpArticlePage = lazyWithRetry(() => import('../pages/Help/HelpArticle'));
const IntegrationEndpointsPage = lazyWithRetry(() => import('../pages/IntegrationEndpoints'));
const ScheduledFunctionsPage = lazyWithRetry(() => import('../pages/ScheduledFunctions'));

const EmailsPage = lazyWithRetry(() => import('../pages/Emails'));

const EmailCampaignPage = lazyWithRetry(() => import('../pages/Emails/Campaign'));

const FunnelCheckoutPage = lazyWithRetry(() => import('../pages/Products/Funnels/FunnelCheckout'));
const PayNowPage = lazyWithRetry(() => import('../pages/Orders/PayNowPage'));

const AllRoutes = () => {
	return (
		<Routes>
			<Route element={<WrapperLayout />}>
				<Route element={<BlankLayout />}>
					<Route exact path={'/agreement/:key'} element={<GenerateAgreementPage />} />
					<Route exact path={ROUTES.AGENT_DASHBOARD + '/:userId'} element={<RequireAuthRep><AgentDashboardPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.AGENT_CHECKOUT + '/:userId'} element={<RequireAuthRep><AgentCheckoutPage /></RequireAuthRep>} />
				</Route>
				<Route element={<AuthLayout />}>
					<Route exact path={ROUTES.SIGN_UP} element={<SignUpPage />} />
				</Route>
				<Route element={<AuthCenteredLayout />}>
					<Route exact path={ROUTES.SIGN_IN} element={<SignInPage />} />
				</Route>
				<Route element={<AuthSplitLayout />}>
					<Route exact path={ROUTES.PASSWORD_FORGET} element={<ForgotPasswordPage />} />
					<Route exact path={ROUTES.PASSWORD_SET} element={<ForgotPasswordPage />} />
					<Route exact path={ROUTES.PASSWORD_RESET} element={<SetNewPasswordPage />} />
				</Route>
				<Route element={<AccountLayout />}>
					<Route exact path={ROUTES.ACCOUNT} element={<RequireAuth><AccountHomePage /></RequireAuth>} />
					<Route exact path={ROUTES.MY_MESSAGES} element={<RequireAuth><ChatPage /></RequireAuth>} />
					<Route exact path={ROUTES.MY_TREATMENTS} element={<RequireAuth><TreatmentsPage /></RequireAuth>} />
					<Route exact path={ROUTES.MY_CHECK_INS} element={<RequireAuth><RequireProgressLogAccess><MyCheckInsPage /></RequireProgressLogAccess></RequireAuth>} />
					<Route exact path={ROUTES.MY_MEAL_PLANS} element={<RequireAuth><MyMealPlansPage /></RequireAuth>} />
					<Route exact path={ROUTES.MY_EVENTS} element={<RequireAuth><RequireSupportGroupsAccess><MyEventsPage /></RequireSupportGroupsAccess></RequireAuth>} />
					<Route exact path={ROUTES.MY_SUPPORT_GROUPS} element={<RequireAuth><RequireSupportGroupsAccess><Navigate to={ROUTES.MY_EVENTS} replace /></RequireSupportGroupsAccess></RequireAuth>} />
					<Route exact path={ROUTES.MY_REFERRALS} element={<RequireAuth><MyReferralsPage /></RequireAuth>} />
					<Route exact path={ROUTES.MY_STORE} element={<RequireAuth><StorePage /></RequireAuth>} />
					<Route exact path={ROUTES.SUBSCRIPTION_REACTIVATION + '/:subscriptionId?'} element={<RequireAuth><SubscriptionReactivationPage /></RequireAuth>} />
					<Route exact path={ROUTES.MY_SUPPORT_TICKETS} element={<RequireAuth><MySupportPage /></RequireAuth>} />
					<Route exact path={ROUTES.PAY_NOW + '/:orderId'} element={<RequireAuth><PayNowPage /></RequireAuth>} />

					<Route exact path={ROUTES.MY_PRESCRIPTIONS} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.MY_PRESCRIPTIONS + '/:orderId'} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.MY_SUBSCRIPTIONS} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.MY_MEDICATION_JOURNAL} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.MY_INVOICES} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.MY_INVOICES + '/:orderId'} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.MY_SUPPORT_TICKET_DETAIL + '/:ticketId'} element={<Navigate to={ROUTES.MY_SUPPORT_TICKETS} replace />} />
					<Route exact path={ROUTES.MY_CONVERSATIONS} element={<Navigate to={ROUTES.MY_MESSAGES} replace />} />
					<Route exact path={ROUTES.PROFILE} element={<Navigate to={ROUTES.ACCOUNT} replace />} />
					<Route exact path={ROUTES.MY_ADDRESSES} element={<Navigate to={ROUTES.ACCOUNT} replace />} />
					<Route exact path={ROUTES.MY_PAYMENT_METHODS} element={<Navigate to={ROUTES.ACCOUNT} replace />} />
					<Route exact path={ROUTES.MY_NOTIFICATIONS} element={<Navigate to={ROUTES.ACCOUNT} replace />} />
					<Route exact path={ROUTES.MY_MFA} element={<RequireAuth><SecurityPage /></RequireAuth>} />
					<Route exact path={ROUTES.PASSWORD_CHANGE} element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
					<Route exact path={ROUTES.CHANGE_PHONE} element={<RequireAuth><ChangePhoneNumberPage /></RequireAuth>} />
					<Route exact path={ROUTES.MY_CONTACT} element={<RequireAuth><ContactPage inDashboard /></RequireAuth>} />
					<Route exact path={ROUTES.MY_HABIT_TRACKER} element={<Navigate to={ROUTES.ACCOUNT} replace />} />
					<Route exact path={ROUTES.SUBSCRIPTION_CANCEL + '/:subscriptionId?'} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.SUBSCRIPTION_CHANGE + '/:subscriptionId?'} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={ROUTES.SUBSCRIPTION_MEDICATION_CHANGE + '/:subscriptionId?'} element={<Navigate to={ROUTES.MY_TREATMENTS} replace />} />
					<Route exact path={'/subscription-reactivation/:subscriptionId?'} element={<RequireAuth><SubscriptionReactivationDeepLinkPage /></RequireAuth>} />
					<Route exact path={'/pay-v2/:orderId'} element={<PayNowRedirect />} />
				</Route>
				<Route element={<HomeLayout />}>
					<Route exact path={ROUTES.USER_ACTION} element={<UserActionPage />} />
					<Route exact path={ROUTES.MFA_SETUP} element={<RequireAuth><AccountSetupPage /></RequireAuth>} />
					<Route exact path={ROUTES.VERIFY_EMAIL} element={<RequireAuth><VerifyEmailPage /></RequireAuth>} />
					<Route exact path={ROUTES.VERIFY_PHONE} element={<RequireAuth><VerifyPhonePage /></RequireAuth>} />

					<Route exact path={ROUTES.REFERRALS} element={<ReferralsPage />} />
					<Route exact path={ROUTES.CONTACT} element={<ContactPage />} />
					<Route exact path={ROUTES.HELP} element={<HelpPage />} />
					<Route exact path={ROUTES.HELP_CATEGORY + '/:categorySlug'} element={<HelpCategoryPage />} />
					<Route exact path={ROUTES.HELP_ARTICLE + '/:articleId'} element={<HelpArticlePage />} />

					<Route exact path={ROUTES.CONSENT} element={<MultipleAgreementsPage agreementKeys={['TELEHEALTH_CONSENT', 'READYRX_PRIVACY_PRACTICES']} />} />
					<Route exact path={ROUTES.PRIVACY} element={<AgreementPage agreementKey={'PRIVACY'} agreementName={'Privacy Policy'} />} />
					<Route exact path={ROUTES.TERMS} element={<AgreementPage agreementKey={'TERMS'} agreementName={'Terms of Use'} />} />
					<Route exact path={ROUTES.COOKIES} element={<AgreementPage agreementKey={'COOKIE'} agreementName={'Cookie Policy'} />} />
					<Route exact path={ROUTES.SUBSCRIPTION_AGREEMENT} element={<AgreementPage agreementKey={'SUBSCRIPTION_AGREEMENT'} agreementName={'Lab Test Order Agreement'} />} />
					<Route exact path={ROUTES.COMMUNICATION_POLICY} element={<AgreementPage agreementKey={'COMMUNICATION'} agreementName={'Communication Policy'} />} />

					<Route exact path={ROUTES.ARTICLE_DETAILS + '/:articleSlug'} element={<BlogPostPage />} />
					<Route exact path={ROUTES.ARTICLE_CATEGORY_DETAILS + '/:articleCategorySlug'} element={<BlogCategoryPage />} />
					<Route exact path={ROUTES.BLOG} element={<BlogPage />} />

					<Route exact path={ROUTES.COVERED_ENTITY + '/:coveredEntityId'} element={<RequireAuthWithCoveredEntityRoles roles={['ADMIN']}><CoveredEntityPage /></RequireAuthWithCoveredEntityRoles>} />
					<Route exact path={ROUTES.AFFILIATE + '/:affiliateId' + '/:subAffiliateId?'} element={<RequireAuthWithAffiliateRoles roles={['ADMIN']}><AffiliatePage /></RequireAuthWithAffiliateRoles>} />

					<Route exact path={ROUTES.COMMUNITY + "/:communitySlug"} element={<RequireAuth><CommunityPage /></RequireAuth>} />
					<Route exact path={ROUTES.COMMUNITY} element={<RequireAuth><BrowseCommunitiesPage /></RequireAuth>} />

					<Route exact path={ROUTES.GROUP + "/:communitySlug"} element={<RequireAuth><GroupPage /></RequireAuth>} />
				</Route>
				<Route element={<AdminLayout />}>
					<Route exact path={ROUTES.ADMIN} element={<RequireAuthAllStaff><StaffDashboardPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_STATISTICS} element={<RequireAuthAdmin><AdminStatisticsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_RETENTION_STATISTICS} element={<RequireAuthAdmin><RetentionStatisticsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_REP_STATISTICS} element={<RequireAuthRep><RepStatisticsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_REP_PERFORMANCE} element={<RequireAuthRepPerformance><RepPerformancePage /></RequireAuthRepPerformance>} />
					<Route exact path={ROUTES.MANAGE_REP_SLA} element={<RequireAuthRepPerformance><RepSlaDashboardPage /></RequireAuthRepPerformance>} />
					<Route exact path={ROUTES.REP_DASHBOARD_PREVIEW} element={<RequireAuthSuperAdmin><RepDashboardPreviewPage /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.MANAGE_FUNNEL_STATISTICS} element={<RequireAuthMarketingManager><FunnelStatisticsPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_FUNNEL_ANALYTICS} element={<RequireAuthMarketingManager><FunnelAnalyticsPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_SITE_VISITS} element={<RequireAuthMarketingManager><SiteVisitsPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_SITE_EVENTS} element={<RequireAuthMarketingManager><SiteEventsPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_SITE_EVENT_STEP_SUMMARY} element={<RequireAuthMarketingManager><SiteEventStepSummaryPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_SALES_BY_STATE} element={<RequireAuthAdmin><SalesByStateDashboardPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_FINANCIALS} element={<RequireAuthAdmin><FinancialsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_COVERED_ENTITY_USERS} element={<RequireAuthAdmin><CoveredEntityUsersPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_REPORTS} element={<RequireAuthAdmin><ReportsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_USERS} element={<RequireAuthRep><UsersPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_AUTH_USERS} element={<RequireAuthAdmin><AuthUsersPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_SUBSCRIPTIONS} element={<RequireAuthRep><SubscriptionsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_SUBSCRIPTIONS_RAW} element={<RequireAuthRep><SubscriptionsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_CANCELLED_SUBSCRIPTIONS} element={<RequireAuthRep><CancelledSubscriptionsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_CANCELLED_SUBSCRIPTIONS_RAW} element={<RequireAuthRep><CancelledSubscriptionsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_CANCELLATION_REQUESTS} element={<RequireAuthRep><CancellationRequestsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_CANCELLATION_REQUESTS_RAW} element={<RequireAuthRep><CancellationRequestsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_ORDERS} element={<RequireAuthRep><OrdersWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_VOUCHER_ORDERS} element={<RequireAuthRep><OrdersWorkspacePage lockedOrderType="VOUCHER" /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_PRESCRIPTION_ORDERS} element={<RequireAuthRep><OrdersWorkspacePage lockedOrderType="PRESCRIPTION" /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_ORDERS_RAW} element={<RequireAuthRep><OrdersPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_ORDER_GROUPS} element={<RequireAuthRep><OrderGroupsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_ORDER_GROUPS_RAW} element={<RequireAuthRep><OrderGroupsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_MESSAGES} element={<RequireAuthRep><MessagesPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_COVERED_ENTITIES} element={<RequireAuthSuperAdmin><CoveredEntitiesPage /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.MANAGE_ENCOUNTERS} element={<RequireAuthRep><EncountersWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_ENCOUNTERS_RAW} element={<RequireAuthRep><EncountersPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_PRESCRIPTIONS} element={<RequireAuthRep><PrescriptionsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_PRESCRIPTIONS_RAW} element={<RequireAuthRep><PrescriptionsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_PRESCRIPTION_GROUPS_RAW} element={<RequireAuthRep><PrescriptionGroupsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_PAYMENTS} element={<RequireAuthRep><PaymentsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_PAYMENTS_RAW} element={<RequireAuthRep><PaymentsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_TRANSACTIONS} element={<RequireAuthRep><TransactionsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_TRANSACTIONS_RAW} element={<RequireAuthRep><TransactionsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_VOUCHERS} element={<RequireAuthRep><VouchersWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_VOUCHERS_RAW} element={<RequireAuthRep><VouchersPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_PROGRAMS} element={<RequireAuthAdmin><ProgramsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_AFFILIATES} element={<RequireAuthMarketingManager><AffiliatesPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_AFFILIATE_USERS} element={<RequireAuthMarketingManager><AffiliateUsersPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_CAMPAIGNS} element={<RequireAuthAdmin><CampaignsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_SMS_TEMPLATES} element={<RequireAuthStaffManager><SMSTemplatesPage /></RequireAuthStaffManager>} />
					<Route exact path={ROUTES.MANAGE_VENDORS} element={<RequireAuthAdmin><VendorsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_SURVEYS} element={<RequireAuthAdmin><SurveysPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_EMAIL_TEMPLATES} element={<RequireAuthStaffManager><EmailTemplatesPage /></RequireAuthStaffManager>} />
					<Route exact path={ROUTES.MANAGE_AGREEMENTS} element={<RequireAuthAdmin><AgreementsPage /></RequireAuthAdmin>} />

					<Route exact path={ROUTES.MANAGE_PRODUCTS} element={<RequireAuthAdmin><ProductsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_ENROLLMENT_GROUPS} element={<RequireAuthAdmin><EnrollmentGroupsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_PRODUCT_FAMILIES} element={<RequireAuthAdmin><ProductFamiliesPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_OFFER_GROUPS} element={<RequireAuthAdmin><OfferGroupsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_COMMERCIAL_EQUIVALENCY_GROUPS} element={<RequireAuthAdmin><CommercialEquivalencyGroupsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_FUNNEL_CHOICE_SETS} element={<RequireAuthAdmin><FunnelChoiceSetsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_MEDICATION_TRANSITION_RULES} element={<RequireAuthAdmin><MedicationTransitionRulesPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_PRODUCT_IDENTITY_AUDIT} element={<RequireAuthAdmin><ProductIdentityAuditPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_ADD_ON_PRODUCTS} element={<RequireAuthAdmin><ProductsPage productTypes={['ADD_ON']} singularEntityName="Add-On Product" pluralEntityName="Add-On Products" /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_PRODUCT_CATEGORIES} element={<RequireAuthAdmin><ProductCategoriesPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_SHIPPING_CLASSES} element={<RequireAuthAdmin><ShippingClassesPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_SHIPPING_METHODS} element={<RequireAuthAdmin><ShippingMethodsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_COUPONS} element={<RequireAuthMarketingManager><CouponsPage /></RequireAuthMarketingManager>} />

					<Route exact path={ROUTES.MANAGE_LEAD_AGGREGATES} element={<RequireAuthAdmin><LeadAggregatesPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_LEAD_AFFILIATE_AGGREGATES} element={<RequireAuthAdmin><LeadAffiliateAggregatesPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_LEADS} element={<RequireAuthRep><LeadsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_LEADS_RAW} element={<RequireAuthRep><LeadsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_LEADS_PENDING_RAW} element={<RequireAuthRep><LeadTablePage tableTitle="Pending Leads" status={['PENDING']} /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_LEADS_ISSUES_RAW} element={<RequireAuthSuperAdmin><LeadTablePage tableTitle="Leads with Issues" status={['SUBMITTED_WITH_ERROR', 'SUBMITTED_WITH_CONTACT_PAYER']} /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.MANAGE_LEADS_SUPPORT_CALL_RAW} element={<RequireAuthSuperAdmin><LeadTablePage tableTitle="Leads To Call" status={'REQUESTED_SUPPORT_CALL'} showNotes={true} /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.MANAGE_LEADS_SUBMITTED_RAW} element={<RequireAuthSuperAdmin><LeadTablePage tableTitle="Leads To Import" status={'SUBMITTED'} /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.MANAGE_LEADS_DUPLICATES_RAW} element={<RequireAuthSuperAdmin><LeadTablePage tableTitle="Duplicate Leads" status={'DUPLICATE'} /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.MANAGE_LEADS_FRAUD_RAW} element={<RequireAuthSuperAdmin><LeadTablePage tableTitle="Suspected Fraud Leads" status={'FRAUD'} /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.MANAGE_COUPON_USES} element={<RequireAuthMarketingManager><CouponUsesPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_ANALYTICS} element={<RequireAuthRep><AnalyticsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_AFFILIATE_CONVERSIONS} element={<RequireAuthMarketingManager><AffiliateConversionsPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_AFFILIATE_PAYOUTS} element={<RequireAuthMarketingManager><AffiliatePayoutsPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_REP_CONVERSIONS} element={<RequireAuthMarketingManager><RepConversionsPage /></RequireAuthMarketingManager>} />
					<Route exact path={ROUTES.MANAGE_REP_PAYOUTS} element={<RequireAuthMarketingManager><RepPayoutsPage /></RequireAuthMarketingManager>} />

					<Route exact path={ROUTES.MANAGE_ADMINS} element={<RequireAuthAdmin><AdminsListPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_REPS} element={<RequireAuthAdmin><RepsListPage /></RequireAuthAdmin>} />

					<Route exact path={ROUTES.MANAGE_ASSETS} element={<RequireAuthAllStaff><AssetsPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_ARTICLES} element={<RequireAuthAllStaff><ArticlesPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_ARTICLE_CATEGORIES} element={<RequireAuthHelpStaff><ArticleCategoriesPage /></RequireAuthHelpStaff>} />

					<Route exact path={ROUTES.MANAGE_CHECK_INS} element={<RequireAuthRep><CheckInsWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_CHECK_INS_RAW} element={<RequireAuthRep><CheckInsPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_MEAL_PLANS} element={<RequireAuthAllStaff><MealPlansWorkspacePage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_MEAL_PLANS_RAW} element={<RequireAuthAllStaff><MealPlansPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_MEALS} element={<RequireAuthAllStaff><MealsWorkspacePage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_MEALS_RAW} element={<RequireAuthAllStaff><MealsPage /></RequireAuthAllStaff>} />

					<Route exact path={ROUTES.MANAGE_ACCOUNTS_PAYABLE_ORDERS} element={<RequireAuthAdmin><AccountsPayableOrdersPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_ACCOUNTS_PAYABLE_ENCOUNTERS} element={<RequireAuthAdmin><AccountsPayableEncountersPage /></RequireAuthAdmin>} />

					<Route exact path={ROUTES.MANAGE_WEBHOOK_LOGS} element={<RequireAuthAdmin><WebhookLogsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_API_LOGS} element={<RequireAuthAdmin><ApiLogsPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_CHANGELOG_LOGS} element={<RequireAuthAdmin><ChangelogLogsPage /></RequireAuthAdmin>} />

					<Route exact path={ROUTES.MANAGE_PROVIDERS} element={<RequireAuthAdmin><ProvidersPage /></RequireAuthAdmin>} />

					<Route exact path={ROUTES.MANAGE_BIOMARKERS} element={<RequireAuthAdmin><BiomarkersPage /></RequireAuthAdmin>} />
					<Route exact path={ROUTES.MANAGE_LAB_PANELS} element={<RequireAuthAdmin><LabPanelsPage /></RequireAuthAdmin>} />

					<Route exact path={ROUTES.MANAGE_EVENTS} element={<RequireAuthAllStaff><EventsWorkspacePage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_EVENTS_RAW} element={<RequireAuthAllStaff><EventsPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_EVENT_CHECK_INS} element={<RequireAuthAllStaff><CheckInsWorkspacePage lockedCheckInType="EVENT" /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_EVENT_CHECK_INS_RAW} element={<RequireAuthAllStaff><CheckInTable tableTitle="Event Sign-Ups" checkInType={["EVENT"]} /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_REFERRAL_CODES} element={<RequireAuthAllStaff><ReferralCodesPage /></RequireAuthAllStaff>} />

					<Route exact path={ROUTES.MANAGE_COMMUNITIES} element={<RequireAuthAllStaff><CommunitiesPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_GROUPS} element={<RequireAuthAllStaff><GroupsPage /></RequireAuthAllStaff>} />

					<Route exact path={ROUTES.MANAGE_TASKS} element={<RequireAuthAllStaff><TasksPage /></RequireAuthAllStaff>} />

					<Route exact path={ROUTES.MANAGE_SUPPORT} element={<RequireAuthRep><SupportDashboardPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_SUPPORT_INBOX} element={<RequireAuthRep><SupportInboxWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_SUPPORT_SPLIT} element={<RequireAuthRep><SupportSplitWorkspacePage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_SUPPORT_OVERSIGHT} element={<RequireAuthStaffManager><SupportInboxOversightPage /></RequireAuthStaffManager>} />
					<Route exact path={ROUTES.SUPPORT_TICKET_DETAIL + '/:ticketId'} element={<RequireAuthRep><SupportTicketDetailPage /></RequireAuthRep>} />
					<Route exact path={ROUTES.MANAGE_SUPPORT_REPLY_TEMPLATES} element={<RequireAuthAllStaff><SupportReplyTemplatesPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_SUPPORT_SNIPPETS} element={<RequireAuthAllStaff><SupportSnippetsPage /></RequireAuthAllStaff>} />

					<Route exact path={ROUTES.MANAGE_AUTOMATIONS} element={<RequireAuthSuperAdmin><AutomationListPage /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.AUTOMATION_DETAIL + '/:ruleId'} element={<RequireAuthSuperAdmin><AutomationDetailPage /></RequireAuthSuperAdmin>} />
					<Route exact path={ROUTES.AUTOMATION_DETAIL} element={<RequireAuthSuperAdmin><AutomationDetailPage /></RequireAuthSuperAdmin>} />

					<Route exact path={ROUTES.MANAGE_KNOWLEDGE_BASE} element={<RequireAuthAllStaff><KnowledgeBasePage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_INTEGRATION_TOOLS} element={<RequireAuthIntegrationTools><IntegrationEndpointsPage /></RequireAuthIntegrationTools>} />
					<Route exact path={ROUTES.MANAGE_SCHEDULED_FUNCTIONS} element={<RequireAuthAdmin><ScheduledFunctionsPage /></RequireAuthAdmin>} />

					<Route exact path={ROUTES.MANAGE_EMAILS} element={<RequireAuthAllStaff><EmailsPage /></RequireAuthAllStaff>} />

					<Route exact path={ROUTES.MANAGE_HABIT_TEMPLATES} element={<RequireAuthAllStaff><HabitTemplatePage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_USER_HABITS} element={<RequireAuthAllStaff><UserHabitPage /></RequireAuthAllStaff>} />
					<Route exact path={ROUTES.MANAGE_WALLETS} element={<RequireAuthAllStaff><WalletPage /></RequireAuthAllStaff>} />

					<Route exact path={ROUTES.MANAGE_EMAIL_CAMPAIGN} element={<RequireAuthAllStaff><EmailCampaignPage /></RequireAuthAllStaff>} />
				</Route>
				<Route element={<EncounterLayout />}>
					<Route exact path={ROUTES.ENCOUNTER + '/:encounterId'} element={<RequireAuth><EncounterPage /></RequireAuth>} />

					<Route exact path={ROUTES.WEIGHT_LOSS_CHECK_IN} element={<RequireAuth><WeightLossCheckInPage /></RequireAuth>} />
					<Route exact path={ROUTES.MEAL_PLAN_SETUP} element={<RequireAuth><MealPlanSetupPage /></RequireAuth>} />
					<Route exact path={ROUTES.MEAL_PLAN_REQUEST} element={<RequireAuth><MealPlanRequestPage /></RequireAuth>} />
					<Route exact path={ROUTES.EVENT_CHECK_IN + '/:checkInId'} element={<RequireAuth><EventCheckInPage /></RequireAuth>} />
				</Route>
				<Route element={<ProductLandingLayout />}>

					{/* <Route exact path={'/glp1'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/se'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/segm'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/ti'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/tigm'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/oz'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/ze'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/semaglutide'} element={<GLP1Page />} />
					<Route exact path={'/shop/p/tirzepatide'} element={<GLP1Page />} />
					<Route exact path={'/shop/c/se'} element={<GLP1Page />} />
					<Route exact path={'/shop/c/semaglutide'} element={<GLP1Page />} />
					<Route exact path={'/shop/c/ti'} element={<GLP1Page />} />
					<Route exact path={'/shop/c/tirzepatide'} element={<GLP1Page />} />
					<Route exact path={'/shop/c/oz'} element={<GLP1Page />} />
					<Route exact path={'/shop/c/ze'} element={<GLP1Page />} />
					<Route exact path={'/glp1'} element={<GLP1Page />} /> */}
					{/* <Route exact path={ROUTES.PROGRAM_FUNNEL + '/c19'} element={<ConditionalRequireAuth paramName="lid"><Covid19FunnelPage /></ConditionalRequireAuth>} /> */}

					{/* <Route exact path={ROUTES.PRODUCT_DETAILS + '/:productShortSku'} element={<ProductDetailsPage />} />
					<Route exact path={ROUTES.PRODUCT_CATEGORY_DETAILS + '/:productShortSku'} element={<ProductDetailsPage />} /> */}
				</Route>
				<Route element={<FunnelLayout />}>
					<Route exact path={ROUTES.PROGRAM_FUNNEL + '/:programSlug' + '/:productSku?'} element={<ConditionalRequireAuth paramName="lid"><ProgramFunnelPage /></ConditionalRequireAuth>} />
					<Route exact path={ROUTES.PROGRAM_FUNNEL_CHECKOUT + '/:programSlug' + '/:productSku?'} element={<ConditionalRequireAuth paramName="lid"><FunnelCheckoutPage /></ConditionalRequireAuth>} />
					{/* <Route exact path={ROUTES.PROGRAM_FUNNEL + '/:programSlug'} element={<ConditionalRequireAuth paramName="lid"><ProgramFunnelPage /></ConditionalRequireAuth>} /> */}
				</Route>

				<Route element={<LandingLayout />}>
					{/* <Route exact path={ROUTES.PRODUCT_CATEGORY_DETAILS + '/c19'} element={<Covid19LandingPage />} /> */}
					{/* <Route exact path={ROUTES.SEMAGLUTIDE_LANDING} element={<SemaglutideLandingPage />} />
					<Route exact path={ROUTES.TIRZEPATIDE_LANDING} element={<TirzepatideLandingPage />} />
					<Route exact path={ROUTES.NADPLUS_LANDING} element={<NadLandingPage />} />
					<Route exact path={ROUTES.GLUTATHIONE_LANDING} element={<GlutathioneLandingPage />} />
					<Route exact path={ROUTES.METFORMIN_LANDING} element={<MetforminLandingPage />} /> */}

					{/* <Route exact path={ROUTES.PROGRAM_DETAILS + '/:programSlug'} element={<HomePage />} /> */}
					{/* <Route exact path={ROUTES.PRODUCT_CATEGORY_DETAILS + '/:productCategorySlug'} element={<HomePage />} /> */}


					<Route exact path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
					<Route exact path={ROUTES.NO_ACCESS} element={<NoAccessPage />} />
					{/* <Route exact path={'/sermorelin'} element={<SermorelinLandingPage />} /> */}
				</Route>
				<Route element={<AccountLayout />}>
					<Route exact path={ROUTES.LANDING} element={<RequireAuth><AccountHomePage /></RequireAuth>} />
					<Route exact path={ROUTES.HOME} element={<Redirect url="https://readyrx.com" />} />
					<Route path={'*'} element={<RequireAuth><AccountHomePage /></RequireAuth>} />
				</Route>
			</Route>
		</Routes>
	);
};

const PROGRAM_LOOKUP_TIMEOUT_MS = 8000;

const RequireSupportGroupsAccess = ({ children }) => {
	const { authContext, profile } = useAuth();
	const { programs, getPrograms } = useOrchestrator();
	const outletContext = useOutletContext() ?? {};
	const subscriptions = Array.isArray(outletContext.subscriptions) ? outletContext.subscriptions : [];
	const subscriptionsLoading = outletContext.subscriptionsLoading === true;
	const isSupportStaff = hasHelpStaffPermissions(authContext);
	const hasProfile = isStringNotEmpty(profile?.userId);
	const hasActiveSubscription = subscriptions.some((sub) => SUPPORT_GROUP_SUBSCRIPTION_STATUSES.includes(sub?.status));
	const needsProgramLookup = hasActiveSubscription && isListEmpty(programs);
	const [hasProgramLookupTimedOut, setHasProgramLookupTimedOut] = useState(false);
	const [programLookupAttempt, setProgramLookupAttempt] = useState(0);
	const supportGroupsAccessDecision = getSupportGroupsAccessDecision({ authContext, subscriptions, programs });

	// programs arrive from the orchestrator and can fail to load at all; deciding without
	// them would deny a program-only patient, so cap the wait and let them retry instead.
	useEffect(() => {
		if (isBooleanFalse(needsProgramLookup)) {
			setHasProgramLookupTimedOut(false);
			return;
		}
		const timeoutId = window.setTimeout(() => setHasProgramLookupTimedOut(true), PROGRAM_LOOKUP_TIMEOUT_MS);
		return () => window.clearTimeout(timeoutId);
	}, [needsProgramLookup, programLookupAttempt]);

	const handleRetryProgramLookup = () => {
		setHasProgramLookupTimedOut(false);
		setProgramLookupAttempt((previousAttempt) => previousAttempt + 1);
		getPrograms(true);
	};

	if (!isSupportStaff && (!hasProfile || subscriptionsLoading || (needsProgramLookup && isBooleanFalse(hasProgramLookupTimedOut)))) {
		return <BrandLoader fullscreen message="Checking support group access..." />;
	}

	if (!isSupportStaff && needsProgramLookup) {
		return (
			<div className="container text-center py-5">
				<h4>We couldn&apos;t verify your access</h4>
				<p>We weren&apos;t able to load your program details. Please try again, or contact support if this keeps happening.</p>
				<button type="button" className="btn btn-primary mt-3" onClick={handleRetryProgramLookup}>
					Try Again
				</button>
			</div>
		);
	}

	if (supportGroupsAccessDecision.canAccess) {
		return children;
	}

	return <Navigate to={ROUTES.ACCOUNT} replace />;
};

const RequireProgressLogAccess = ({ children }) => {
	const { authContext, profile } = useAuth();
	const { programs, getPrograms } = useOrchestrator();
	const outletContext = useOutletContext() ?? {};
	const subscriptions = Array.isArray(outletContext.subscriptions) ? outletContext.subscriptions : [];
	const subscriptionsLoading = outletContext.subscriptionsLoading === true;
	const hasProfile = isStringNotEmpty(profile?.userId);
	const progressLogAccessDecision = getProgressLogAccessDecision({ authContext, subscriptions, programs });
	// a weight-loss SKU decides on its own, so programs are only needed for a subscription that
	// has neither a recognised SKU nor a resolved program
	const needsProgramLookup = isBooleanFalse(progressLogAccessDecision.canAccess)
		&& subscriptions.some((sub) => PROGRESS_LOG_SUBSCRIPTION_STATUSES.includes(sub?.status))
		&& isListEmpty(programs);
	const [hasProgramLookupTimedOut, setHasProgramLookupTimedOut] = useState(false);
	const [programLookupAttempt, setProgramLookupAttempt] = useState(0);

	// programs arrive from the orchestrator and can fail to load at all; deciding without
	// them would deny a weight-loss patient whose enrollment is only expressed as a programId,
	// so cap the wait and let them retry instead.
	useEffect(() => {
		if (isBooleanFalse(needsProgramLookup)) {
			setHasProgramLookupTimedOut(false);
			return;
		}
		const timeoutId = window.setTimeout(() => setHasProgramLookupTimedOut(true), PROGRAM_LOOKUP_TIMEOUT_MS);
		return () => window.clearTimeout(timeoutId);
	}, [needsProgramLookup, programLookupAttempt]);

	const handleRetryProgramLookup = () => {
		setHasProgramLookupTimedOut(false);
		setProgramLookupAttempt((previousAttempt) => previousAttempt + 1);
		getPrograms(true);
	};

	if (isBooleanFalse(progressLogAccessDecision.isRep) && (!hasProfile || subscriptionsLoading || (needsProgramLookup && isBooleanFalse(hasProgramLookupTimedOut)))) {
		return <BrandLoader fullscreen message="Checking progress log access..." />;
	}

	if (isBooleanFalse(progressLogAccessDecision.isRep) && needsProgramLookup) {
		return (
			<div className="container text-center py-5">
				<h4>We couldn&apos;t verify your access</h4>
				<p>We weren&apos;t able to load your program details. Please try again, or contact support if this keeps happening.</p>
				<button type="button" className="btn btn-primary mt-3" onClick={handleRetryProgramLookup}>
					Try Again
				</button>
			</div>
		);
	}

	if (progressLogAccessDecision.canAccess) {
		return children;
	}

	return <Navigate to={ROUTES.ACCOUNT} replace />;
};

const PayNowRedirect = () => {
	const location = useLocation();
	const { orderId } = useParams();

	return <Navigate to={{ pathname: `${ROUTES.PAY_NOW}/${orderId}`, search: location.search }} replace />;
};

const Redirect = ({ url }) => {
	//#region useSearchParams

	const [searchParams, setSearchParams] = useSearchParams();

	//#endregion

	useEffect(() => {
		//document.getElementById("anchorID").click();
		window.location.href = url + "?" + searchParams.toString();
	}, [url, searchParams]);

	return (
		<>
			<h5>Redirecting...</h5>
			<a id="anchorID" href={url + "?" + searchParams.toString()} target="_blank">Click here if you are not redirected back to the store.</a>
		</>
	);
};

export default AllRoutes;
